/**
 * Repeat intervals for scheduled messages, in the words staff use for them.
 *
 * A schedule stores its interval as whole seconds, which is what the backend
 * counts in, but nobody thinks in 5400. People type "1h 30m", read "every
 * hour" in a list, and pick "90 minutes" from a number box and a unit menu, so
 * this converts between the three without losing a second on the way: what
 * `formatInterval` writes, `parseInterval` reads back to the same number.
 *
 * Parsing does not enforce the bounds. "30s" is a real interval that is too
 * short, which deserves a different message from "soon", so callers check
 * MIN_INTERVAL_SECONDS and MAX_INTERVAL_SECONDS themselves.
 */

/** Shortest repeat the scheduler accepts: one minute. */
export const MIN_INTERVAL_SECONDS = 60;
/** Longest repeat the scheduler accepts: 90 days. */
export const MAX_INTERVAL_SECONDS = 7_776_000;

export type IntervalUnit = 'minutes' | 'hours' | 'days';

const UNIT_SECONDS: Record<IntervalUnit, number> = { minutes: 60, hours: 3_600, days: 86_400 };

const PARTS = [
  { seconds: 86_400, short: 'd', long: 'day' },
  { seconds: 3_600, short: 'h', long: 'hour' },
  { seconds: 60, short: 'm', long: 'minute' },
  { seconds: 1, short: 's', long: 'second' },
] as const;

const COMPACT = /^(?:(\d+)\s*d)?\s*(?:(\d+)\s*h)?\s*(?:(\d+)\s*m)?\s*(?:(\d+)\s*s)?$/i;

/**
 * Seconds from a compact interval such as "15m", "1h30m", "1h 30m", "90s" or
 * "2d" (units in that order, any case), or null for anything else.
 */
export function parseInterval(input: string): number | null {
  if (typeof input !== 'string') return null;
  const match = COMPACT.exec(input.trim());
  if (!match) return null;
  const amounts = match.slice(1);
  if (amounts.every(amount => amount === undefined)) return null;
  const total = amounts.reduce(
    (sum, amount, index) => sum + (amount === undefined ? 0 : Number(amount) * PARTS[index].seconds),
    0,
  );
  return Number.isSafeInteger(total) ? total : null;
}

/** Whole, non-negative seconds split into days, hours, minutes and seconds. */
function breakDown(seconds: number): number[] {
  let rest = Number.isFinite(seconds) ? Math.max(0, Math.round(seconds)) : 0;
  return PARTS.map(part => {
    const amount = Math.floor(rest / part.seconds);
    rest -= amount * part.seconds;
    return amount;
  });
}

/** "15m", "1h 30m", "2d", "1m 30s". Zero is "0s". */
export function formatInterval(seconds: number): string {
  const amounts = breakDown(seconds);
  const parts = PARTS.flatMap((part, index) => (amounts[index] > 0 ? [`${amounts[index]}${part.short}`] : []));
  return parts.length > 0 ? parts.join(' ') : '0s';
}

/** For sentences: "every 15 minutes", "every hour", "every 2 days", "every 1 hour 30 minutes". */
export function describeInterval(seconds: number): string {
  const amounts = breakDown(seconds);
  const parts = PARTS.flatMap((part, index) => (amounts[index] > 0 ? [{ amount: amounts[index], unit: part.long }] : []));
  if (parts.length === 0) return 'every 0 seconds';
  if (parts.length === 1 && parts[0].amount === 1) return `every ${parts[0].unit}`;
  return `every ${parts.map(({ amount, unit }) => `${amount} ${unit}${amount === 1 ? '' : 's'}`).join(' ')}`;
}

/**
 * The interval as a number and a unit for a number box and a unit menu: the
 * largest unit that divides it evenly, otherwise minutes rounded up (so an
 * interval under a minute shows as 1 minute).
 */
export function splitInterval(seconds: number): { value: number; unit: IntervalUnit } {
  const total = Number.isFinite(seconds) ? Math.max(0, Math.round(seconds)) : 0;
  if (total > 0 && total % UNIT_SECONDS.days === 0) return { value: total / UNIT_SECONDS.days, unit: 'days' };
  if (total > 0 && total % UNIT_SECONDS.hours === 0) return { value: total / UNIT_SECONDS.hours, unit: 'hours' };
  return { value: Math.ceil(total / UNIT_SECONDS.minutes), unit: 'minutes' };
}

/** Seconds from a number and a unit, the inverse of `splitInterval`. */
export function joinInterval(value: number, unit: IntervalUnit): number {
  return Number.isFinite(value) ? Math.round(value * UNIT_SECONDS[unit]) : 0;
}
